import React, { useRef, useEffect } from 'react';

/* Yandex Maps JS API (v2.1) — loaded lazily and shared across all
   YandexMap instances on the page. */
let _ymapsPromise = null;
export function loadYmaps() {
  if (_ymapsPromise) return _ymapsPromise;
  if (typeof window !== 'undefined' && window.ymaps) {
    _ymapsPromise = Promise.resolve(window.ymaps);
    return _ymapsPromise;
  }
  _ymapsPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('ymaps-script');
    const onReady  = () => { window.ymaps.ready(() => resolve(window.ymaps)); };
    if (existing) { existing.addEventListener('load', onReady, { once: true }); return; }
    const s = document.createElement('script');
    s.id     = 'ymaps-script';
    s.src    = 'https://api-maps.yandex.ru/2.1/?lang=ru_RU';
    s.async  = true;
    s.onload = onReady;
    s.onerror = () => reject(new Error('ymaps load failed'));
    document.head.appendChild(s);
  });
  return _ymapsPromise;
}

/**
 * YandexMap — renders a map with one pin per coord. Auto-fits bounds.
 *
 * Props:
 *   points        — [{ lat, lon, name?, label?, address? }]
 *   singleZoom    — zoom when only one point (default 16)
 *   className     — host template's wrapper class (styling is the
 *                   template's responsibility; the component itself
 *                   doesn't ship any CSS).
 *   preset        — ymaps placemark preset (default 'islands#violetIcon')
 *   controls      — ymaps controls array (default
 *                   ['zoomControl','geolocationControl','fullscreenControl'])
 */
const DEFAULT_CONTROLS = ['zoomControl', 'geolocationControl', 'fullscreenControl'];

export default function YandexMap({
  points,
  singleZoom = 16,
  className,
  preset = 'islands#violetIcon',
  controls,
  region = 'ru',
  provider,
}) {
  const ref = useRef(null);
  /* provider ('yandex' | 'google') — явный выбор картографа в блоке «Локация»;
     имеет приоритет над регионом. Без него: Беларусь/Казахстан → Google. */
  const isGoogle = provider
    ? provider === 'google'
    : (region === 'by' || region === 'kz');
  /* Stable string key for the points array. Without it, the parent's
     `venues.filter(...)` call returns a new array reference on every
     render — causing the useEffect to fire constantly, which destroys
     and rebuilds the map (or in the worst case cancels initialisation
     before the async ymaps.load resolves, leaving an empty container). */
  const pointsKey = points
    ? points.map(p => `${p.lat},${p.lon}`).join('|')
    : '';
  const ctrlsKey = (controls || DEFAULT_CONTROLS).join('|');

  useEffect(() => {
    let map = null;
    let cancelled = false;
    if (isGoogle || !ref.current || !points?.length) return;
    loadYmaps().then((ymaps) => {
      if (cancelled || !ref.current) return;
      const first = points[0];
      map = new ymaps.Map(ref.current, {
        center:   [+first.lat, +first.lon],
        zoom:     singleZoom,
        controls: controls || DEFAULT_CONTROLS,
      }, { suppressMapOpenBlock: true });
      points.forEach((p, idx) => {
        const mark = new ymaps.Placemark(
          [+p.lat, +p.lon],
          {
            balloonContentHeader: p.name || p.label || `Точка ${idx + 1}`,
            balloonContentBody:   p.address || '',
          },
          { preset }
        );
        map.geoObjects.add(mark);
      });
      if (points.length > 1) {
        map.setBounds(map.geoObjects.getBounds(), {
          checkZoomRange: true,
          zoomMargin:     40,
        });
      }
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (map) { try { map.destroy(); } catch {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointsKey, singleZoom, preset, ctrlsKey, isGoogle]);

  if (isGoogle) {
    const pts = (points || []).filter(p => p && p.lat != null && p.lon != null);
    if (!pts.length) {
      return <div className={className} style={{ width: '100%', height: '100%' }} />;
    }
    const ll = p => `${(+p.lat).toFixed(6)},${(+p.lon).toFixed(6)}`;
    /* Несколько точек → directions-эмбед (все точки как остановки маршрута,
       у каждой пин), один пин — обычный q=. Оба варианта без API-ключа. */
    const src = pts.length > 1
      ? `https://maps.google.com/maps?saddr=${ll(pts[0])}&daddr=${pts.slice(1).map(ll).join('+to:')}&hl=ru&output=embed`
      : `https://maps.google.com/maps?q=${ll(pts[0])}&z=${singleZoom}&hl=ru&output=embed`;
    return (
      <iframe
        title="map"
        className={className}
        src={src}
        style={{ width: '100%', height: '100%', border: 0 }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    );
  }
  return <div ref={ref} className={className} style={{ width: '100%', height: '100%' }} />;
}
